import type { CSSProperties } from 'react';

import { costValueColor, Drawer, DrawerHeader, focusRing, Icon } from '@/design-system';
import type { CostKey, PublicCosts, PublicLanguage } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { formatAmount } from '../format';
import { costBreakdown, entryTitle, type FullReportIndex, type IndexedEntry } from '../reportIndex';

export interface CostBreakdownDrawerProps {
    /** Het blok waarvan de bevindingen open staan; `null`: dicht. */
    costKey: CostKey | null;
    costs: PublicCosts;
    index: FullReportIndex;
    language: PublicLanguage;
    copy: PublicCopy;
    /** De huisstijl: de lade opent buiten de pagina. */
    brandStyle: CSSProperties;
    onClose: () => void;
    /** Naar de bevinding in het rapport. */
    onShow: (indexed: IndexedEntry) => void;
}

/**
 * De bevindingen achter één bedrag: welke het zijn, waar ze staan en wat elk
 * kost, met onder de streep het totaal - zichtbaar dezelfde som als op het
 * blok. Een tik op een bevinding sluit de lade en toont hem in het rapport.
 *
 * Een lade, geen venster in het midden: zo werkt elke laag in deze app. Hij
 * vangt de focus, sluit met Escape en geeft de focus terug aan het blok.
 */
export function CostBreakdownDrawer({
    costKey,
    costs,
    index,
    language,
    copy,
    brandStyle,
    onClose,
    onShow,
}: CostBreakdownDrawerProps) {
    const block = costKey === null ? undefined : costs.blocks.find((item) => item.key === costKey);

    if (costKey === null || !block) {
        return null;
    }

    const money = (amount: number) => formatAmount(amount, language);
    const breakdown = costBreakdown(index, costKey, block.amount);
    const title = `${copy.costLabels[costKey]} · ${money(block.amount)}`;

    return (
        <Drawer
            open
            onClose={onClose}
            side="right"
            width={480}
            title={title}
            style={brandStyle}
            className="max-w-full"
        >
            <DrawerHeader
                eyebrow={copy.costBreakdownEyebrow}
                title={title}
                description={copy.costBreakdownIntro}
                onClose={onClose}
                closeLabel={copy.costBreakdownClose}
            />

            <div className="px-4 pt-2 pb-6">
                <ol className="m-0 flex list-none flex-col divide-y divide-[var(--border-subtle-2)] rounded-[10px] border border-[var(--border-default)] p-0">
                    {breakdown.rows.map(({ indexed, amount }) => {
                        const name = entryTitle(indexed.entry);

                        return (
                            <li key={indexed.anchor} className="m-0">
                                <button
                                    type="button"
                                    onClick={() => onShow(indexed)}
                                    className={cn(
                                        'flex min-h-[56px] w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-3 py-3 text-left',
                                        'transition-colors duration-100 hover:bg-[var(--surface-row-hover)]',
                                        focusRing,
                                    )}
                                >
                                    <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
                                        <span className="text-[14px] leading-[1.35] font-medium break-words text-[var(--text-primary)]">
                                            {name}
                                        </span>
                                        <span className="text-[12px] leading-[1.35] break-words text-[var(--text-tertiary-safe)]">
                                            {indexed.place}
                                        </span>
                                    </span>
                                    <span className="shrink-0 font-[family-name:var(--font-mono)] text-[13px] whitespace-nowrap text-[var(--text-primary)]">
                                        {money(amount)}
                                    </span>
                                    <Icon
                                        name="chevronDown"
                                        size="sm"
                                        tone="muted"
                                        className="shrink-0 -rotate-90"
                                    />
                                </button>
                            </li>
                        );
                    })}

                    {breakdown.rest !== 0 ? (
                        <li className="m-0 flex min-h-[48px] items-center gap-3 px-3 py-3">
                            <span className="min-w-0 flex-1 text-[14px] leading-[1.35] text-[var(--text-secondary)]">
                                {copy.costBreakdownRest}
                            </span>
                            <span className="shrink-0 font-[family-name:var(--font-mono)] text-[13px] whitespace-nowrap text-[var(--text-primary)]">
                                {money(breakdown.rest)}
                            </span>
                            {/* Even breed als het pijltje erboven, zodat de bedragen onder elkaar staan. */}
                            <span aria-hidden="true" className="w-4 shrink-0" />
                        </li>
                    ) : null}
                </ol>

                <div className="mt-3 flex items-baseline gap-3 border-t-2 border-[var(--border-default)] px-3 pt-3">
                    <span className="min-w-0 flex-1 text-[14px] font-medium text-[var(--text-primary)]">
                        {copy.costBreakdownTotal}
                    </span>
                    <span
                        className="shrink-0 font-[family-name:var(--font-mono)] text-[14px] font-semibold whitespace-nowrap"
                        style={{ color: costValueColor[costKey] }}
                    >
                        {money(breakdown.total)}
                    </span>
                    <span aria-hidden="true" className="w-4 shrink-0" />
                </div>
            </div>
        </Drawer>
    );
}

const stripStyles: Record<CostKey, string> = {
    direct: 'border-[var(--danger-border)] bg-[var(--danger-tint-2)]',
    termijn: 'border-[var(--warn-border)] bg-[var(--warn-tint-2)]',
    onderhoud: 'border-[var(--border-default)] bg-[var(--surface-card)]',
};

const stripLabels: Record<CostKey, string> = {
    direct: 'text-[var(--danger-text)]',
    termijn: 'text-[var(--warn-text)]',
    onderhoud: 'text-[var(--text-tertiary-safe)]',
};

/**
 * De drie bedragen in de samenvatting bovenaan het overzicht, klein, elk een
 * knop naar de bevindingen erachter. Dezelfde kleuren als de blokken van het
 * tabblad Kosten.
 */
export function CostStrip({
    costs,
    index,
    language,
    copy,
    onShow,
    className,
}: {
    costs: PublicCosts;
    index: FullReportIndex;
    language: PublicLanguage;
    copy: PublicCopy;
    onShow: (key: CostKey) => void;
    className?: string;
}) {
    if (!costs.visible || costs.blocks.length === 0) {
        return null;
    }

    return (
        <div className={cn('grid grid-cols-[repeat(auto-fit,minmax(104px,1fr))] gap-2', className)}>
            {costs.blocks.map((block) => {
                const amount = formatAmount(block.amount, language);
                const count = costBreakdown(index, block.key, block.amount).rows.length;

                return (
                    <button
                        key={block.key}
                        type="button"
                        aria-haspopup="dialog"
                        aria-label={copy.costBreakdownOpen(copy.costLabels[block.key], amount)}
                        disabled={count === 0}
                        onClick={() => onShow(block.key)}
                        className={cn(
                            'flex min-h-[44px] cursor-pointer flex-col items-start gap-[2px] rounded-[9px] border px-3 py-[9px] text-left',
                            'transition-colors duration-100 hover:border-[var(--accent)] disabled:cursor-default disabled:hover:border-[var(--border-default)]',
                            stripStyles[block.key],
                            focusRing,
                        )}
                    >
                        <span className={cn('text-[12px] leading-[1.3]', stripLabels[block.key])}>
                            {copy.costLabels[block.key]}
                        </span>
                        <span
                            className="font-[family-name:var(--font-display)] text-[17px] leading-[1.2] font-semibold"
                            style={{ color: costValueColor[block.key] }}
                        >
                            {amount}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
